/**
 * <pvz-cart-added>
 *
 * 商品詳細のカート追加シート (Figma: 商品詳細 - カート追加)。
 * 商品フォームの送信を横取りして /cart/add.js に投げ、成功したら
 * 追加した商品の情報を入れて下からせり上げる。
 *
 * 通信に失敗した場合は preventDefault せず、従来どおり通常のフォーム送信に任せる。
 */
class PerverzeCartAdded extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById('cpd-form') || document.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;

    this.onSubmit = this.onSubmit.bind(this);
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.form.addEventListener('submit', this.onSubmit);
    this.querySelectorAll('[data-pvz-cart-close]').forEach((el) => el.addEventListener('click', this.close));
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
  }

  onKeydown(event) {
    if (event.key === 'Escape' && !this.hidden) this.close();
  }

  async onSubmit(event) {
    event.preventDefault();

    const button = this.form.querySelector('[name="add"]');
    if (button) button.setAttribute('aria-busy', 'true');

    try {
      const body = new FormData(this.form);
      // sections を要求しないシンプルな追加。返り値の item から表示内容を作る
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const item = await response.json();
      if (!response.ok) throw new Error(item.description || 'add to cart failed');

      this.render(item);
      // ポップアップはスマホだけ。PC ではヘッダーのカート表示の更新のみ行う。
      if (this.isMobile()) this.open();
      this.updateCartCount();
    } catch (e) {
      // 失敗時は通常のフォーム送信にフォールバック
      this.form.submit();
    } finally {
      if (button) button.removeAttribute('aria-busy');
    }
  }

  /** ポップアップを出すかどうか (テーマのブレークポイントに合わせる) */
  isMobile() {
    return window.matchMedia('(max-width: 749.98px)').matches;
  }

  render(item) {
    const img = this.querySelector('[data-pvz-cart-image]');
    if (img) {
      if (item.image) {
        img.src = item.image;
        img.alt = item.product_title || '';
        img.removeAttribute('hidden');
      } else {
        img.setAttribute('hidden', '');
      }
    }

    const name = this.querySelector('[data-pvz-cart-name]');
    // 商品名は「商品名 / カラー名」形式。カラーは下の行に出すのでここでは外す
    if (name) name.textContent = (item.product_title || '').split(' / ')[0];

    const options = this.querySelector('[data-pvz-cart-options]');
    if (options) {
      options.textContent = '';
      (item.options_with_values || []).forEach((opt) => {
        const row = document.createElement('span');
        row.className = 'pvz-cart-added__meta-row';
        row.textContent = `${opt.name.toUpperCase()}：${opt.value}`;
        options.appendChild(row);
      });
    }

    const price = this.querySelector('[data-pvz-cart-price]');
    if (price) price.textContent = this.formatMoney(item.final_line_price ?? item.final_price ?? item.price);
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    // 日本円 (小数なし) 前提。Shopify の money フォーマットに合わせて ¥ + 桁区切り
    return `¥${Math.round(cents / 100).toLocaleString('ja-JP')}`;
  }

  // ヘッダーのカート個数を更新する (custom-header の表示に合わせる)
  async updateCartCount() {
    try {
      const res = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      document.querySelectorAll('[data-chdr-cart-count]').forEach((el) => {
        el.textContent = cart.item_count > 9 ? '9+' : String(cart.item_count);
        el.hidden = cart.item_count === 0;
      });
      // 0 件のときはカートアイコンごと隠す
      document.querySelectorAll('[data-chdr-cart-link]').forEach((el) => {
        el.hidden = cart.item_count === 0;
      });
    } catch (e) {
      /* 個数表示の更新に失敗しても本体の動作には影響させない */
    }
  }

  open() {
    this.hidden = false;
    // 次のフレームでクラスを付けてトランジションさせる
    requestAnimationFrame(() => this.classList.add('is-open'));
    document.body.classList.add('pvz-cart-added-open');
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('pvz-cart-added-open');
    setTimeout(() => {
      this.hidden = true;
    }, 300);
  }
}

customElements.define('pvz-cart-added', PerverzeCartAdded);
